// src/components/ui/toast/hooks/useToastTimer.ts
"use client";

import { useEffect, useRef, useState } from "react";

/**
 * useToastTimer
 * -------------
 * Encapsulates timing behavior for a single toast:
 * - Auto-dismiss after `duration`
 * - Pause/resume on hover
 * - Exposes remaining time (ms) to drive the progress bar
 *
 * Implementation notes:
 * - No promises in render; timers are created in effects.
 * - Uses a small 100ms tick for good enough accuracy with low CPU usage.
 */
export function useToastTimer(duration: number, onTimeout: () => void) {
    const [paused, setPaused] = useState(false);
    const [remainingMs, setRemainingMs] = useState(duration);
    // Use refs for values that don't need to trigger re-renders
    const startTimeRef = useRef<number | null>(null);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        // Initialize start time in effect to avoid calling Date.now() during render
        if (startTimeRef.current === null) {
            startTimeRef.current = Date.now();
        }

        const tick = () => {
            if (paused) {
                timerRef.current = window.setTimeout(tick, 100) as unknown as number;
                return;
            }
            if (startTimeRef.current === null) return;
            const elapsed = Date.now() - startTimeRef.current;
            const remaining = duration - elapsed;
            const newRemaining = Math.max(remaining, 0);
            setRemainingMs(newRemaining);
            if (remaining <= 0) {
                onTimeout();
            } else {
                timerRef.current = window.setTimeout(tick, 100) as unknown as number;
            }
        };

        timerRef.current = window.setTimeout(tick, 100) as unknown as number;
        return () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
        };
    }, [duration, paused, onTimeout]);

    const onMouseEnter = () => setPaused(true);
    const onMouseLeave = () => {
        // shift the start time so the remaining time continues accurately
        if (startTimeRef.current !== null) {
            startTimeRef.current = Date.now() - (duration - remainingMs);
        }
        setPaused(false);
    };

    return {
        paused,
        remainingMs,
        eventHandlers: { onMouseEnter, onMouseLeave },
    } as const;
}
